import { useState, useMemo, useEffect, useRef, type MouseEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { getHistoryState, type HistoryLesson, type HistoryQuiz, type HistoryTopic } from '@/mocks/historyStore';
import { getScanHistory, renameScan, type ScanHistoryItem } from '@/mocks/scanHistory';
import { renameStudySession } from '@/mocks/studySessions';
import { savedSubjects } from '@/mocks/subjects';
import Pressable from '@/components/base/Pressable';
import { listChatConversations, type ChatConversation } from '@/lib/chatHistory';

type FilterType = 'all' | 'lessons' | 'quizzes' | 'topics' | 'scans' | 'chats';

const subjectColorMap: Record<string, string> = {
  Mathematics: '#a5a5aa',
  'Physical Sciences': '#b7b7bc',
  'Life Sciences': '#939398',
  Accounting: '#c5c5ca',
  CAT: '#a0a0a5',
};

function formatTimeDisplay(iso: string): string {
  const now = new Date();
  const then = new Date(iso);
  const diff = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

type UnifiedItem =
  | { kind: 'lesson'; data: HistoryLesson }
  | { kind: 'quiz'; data: HistoryQuiz }
  | { kind: 'topic'; data: HistoryTopic }
  | { kind: 'scan'; data: ScanHistoryItem }
  | { kind: 'chat'; data: ChatConversation };

function getItemStatus(item: UnifiedItem): string {
  switch (item.kind) {
    case 'lesson': {
      const p = item.data.completionPercent;
      if (p === 100) return 'Completed';
      if (p > 0) return 'In Progress';
      return 'Recently Generated';
    }
    case 'quiz':
      return 'Completed';
    case 'topic':
      return 'Viewed';
    case 'scan':
      return item.data.status === 'processing' || item.data.status === 'incomplete' ? 'Incomplete'
        : item.data.status === 'failed' ? 'Failed'
          : item.data.deliveryMode === 'full_audio_fallback' ? 'Completed · Full-audio fallback' : 'Completed';
    case 'chat':
      return 'Chat';
  }
}

function getItemUploadType(item: UnifiedItem): string {
  switch (item.kind) {
    case 'lesson':
      return 'AI Lesson';
    case 'quiz':
      return 'Quiz';
    case 'topic':
      return 'Topic';
    case 'scan':
      return `${item.data.uploadType} Scan`;
    case 'chat':
      return item.data.source === 'lesson' ? 'Lesson chat' : 'AI Tutor';
  }
}

function getItemTitle(item: UnifiedItem): string {
  switch (item.kind) {
    case 'lesson':
      return item.data.title;
    case 'quiz':
      return item.data.title;
    case 'topic':
      return item.data.name;
    case 'scan':
      return item.data.title;
    case 'chat':
      return item.data.title;
  }
}

function getItemSubject(item: UnifiedItem): string {
  if (item.kind === 'chat') return item.data.lessonContext?.subject ?? 'AI Assistant';
  return item.data.subject;
}

function getItemDate(item: UnifiedItem): string {
  switch (item.kind) {
    case 'lesson':
      return item.data.lastWatchedAt;
    case 'quiz':
      return item.data.completedAt;
    case 'topic':
      return item.data.openedAt;
    case 'scan':
      return item.data.createdAt;
    case 'chat':
      return item.data.updatedAt;
  }
}

function getItemProgress(item: UnifiedItem): number | null {
  switch (item.kind) {
    case 'lesson':
      return item.data.completionPercent;
    case 'scan':
      return item.data.status === 'failed' ? null
        : item.data.status === 'processing' || item.data.status === 'incomplete' ? item.data.progress ?? 0 : 100;
    default:
      return null;
  }
}

function getItemIcon(item: UnifiedItem): string {
  switch (item.kind) {
    case 'lesson':
      return 'ri-video-line';
    case 'quiz':
      return 'ri-questionnaire-line';
    case 'topic':
      return 'ri-book-2-line';
    case 'scan':
      return item.data.type === 'text' ? 'ri-file-text-line' : 'ri-movie-line';
    case 'chat':
      return 'ri-chat-3-line';
  }
}

function getItemMessageCount(item: UnifiedItem): number | null {
  return item.kind === 'chat' ? item.data.messages.length : null;
}

function statusStyle(status: string, isDark: boolean): string {
  if (status === 'Completed') return isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600';
  if (status === 'In Progress' || status === 'Incomplete') return isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600';
  if (status === 'Viewed') return isDark ? 'bg-[#3A3A3C] text-[#A1A1A6]' : 'bg-neutral-100 text-neutral-500';
  return isDark ? 'bg-[#3A3A3C] text-[#A1A1A6]' : 'bg-neutral-100 text-neutral-500';
}

export default function History() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { user } = useAuth();
  const isDark = resolvedTheme === 'dark';
  const scrollRef = useRef<HTMLDivElement>(null);

  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState(() => getHistoryState());
  const [scanHistory, setScanHistory] = useState(() => getScanHistory());
  const [chatConversations, setChatConversations] = useState<ChatConversation[]>(() => listChatConversations(user?.id));
  const [refresh, setRefresh] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeQuiz, setActiveQuiz] = useState<HistoryQuiz | null>(null);
  const [editValue, setEditValue] = useState('');

  const textPrimary = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-[#A1A1A6]' : 'text-neutral-400';
  const textMuted = isDark ? 'text-[#8E8E93]' : 'text-neutral-500';
  const textFaint = isDark ? 'text-[#48484F]' : 'text-neutral-300';
  const cardBg = isDark ? 'bg-[#1C1D21]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/[0.05]' : 'border-neutral-100/80';
  const rowBg = isDark ? 'bg-[#1C1D21]' : 'bg-white';
  const rowBorder = isDark ? 'border-white/[0.06]' : 'border-neutral-100';
  const iconBg = isDark ? 'bg-white/[0.04]' : 'bg-neutral-100';

  const totalLessons = history.lessons.length;
  const totalQuizzes = history.quizzes.length;
  const totalTopics = history.topics.length;
  const totalSaved = history.savedLessonIds.length;
  const totalScans = scanHistory.length;
  const totalChats = chatConversations.length;
  const hasAnyStats = totalLessons + totalQuizzes + totalTopics + totalSaved + totalScans + totalChats > 0;

  useEffect(() => {
    if (location.pathname === '/history') {
      setHistory(getHistoryState());
      setChatConversations(listChatConversations(user?.id));
      setScanHistory(getScanHistory());
    }
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (location.pathname === '/history') setQuery(new URLSearchParams(location.search).get('q') || '');
  }, [location.pathname, location.search]);

  const unifiedItems: UnifiedItem[] = useMemo(() => {
    const items: UnifiedItem[] = [];

    if (activeFilter === 'all' || activeFilter === 'lessons') {
      history.lessons.forEach((l) => items.push({ kind: 'lesson', data: l }));
    }
    if (activeFilter === 'all' || activeFilter === 'quizzes') {
      history.quizzes.forEach((q) => items.push({ kind: 'quiz', data: q }));
    }
    if (activeFilter === 'all' || activeFilter === 'topics') {
      history.topics.forEach((t) => items.push({ kind: 'topic', data: t }));
    }
    if (activeFilter === 'all' || activeFilter === 'scans') {
      scanHistory.forEach((s) => items.push({ kind: 'scan', data: s }));
    }
    if (activeFilter === 'all' || activeFilter === 'chats') {
      chatConversations.forEach((chat) => items.push({ kind: 'chat', data: chat }));
    }

    items.sort((a, b) => new Date(getItemDate(b)).getTime() - new Date(getItemDate(a)).getTime());
    const term = query.trim().toLowerCase();
    return term ? items.filter((item) => `${getItemTitle(item)} ${getItemSubject(item)} ${getItemUploadType(item)}`.toLowerCase().includes(term)) : items;
  }, [history, scanHistory, chatConversations, activeFilter, refresh, query]);

  const hasContent = unifiedItems.length > 0;

  const subjectIconMap: Record<string, string> = {};
  savedSubjects.forEach((s) => {
    subjectIconMap[s.name] = s.icon;
  });

  // Restore scroll position on mount
  useEffect(() => {
    const saved = sessionStorage.getItem('history_scroll_pos');
    if (saved && scrollRef.current) {
      scrollRef.current.scrollTop = parseInt(saved, 10);
    }
  }, []);

  function handlePress(item: UnifiedItem) {
    if (editingId) return;
    // Save scroll position before navigating
    if (scrollRef.current) {
      sessionStorage.setItem('history_scroll_pos', String(scrollRef.current.scrollTop));
    }
    switch (item.kind) {
      case 'lesson':
        navigate(`/lesson/${item.data.id}`);
        break;
      case 'quiz':
        setActiveQuiz(item.data);
        break;
      case 'topic':
        navigate(`/topic/${item.data.id}`);
        break;
      case 'scan':
        if (item.data.status === 'processing' || item.data.status === 'incomplete') {
          if (item.data.type === 'video' && item.data.generationId) {
            navigate(`/scan/analyzing?resume=${encodeURIComponent(item.data.generationId)}`);
          }
          break;
        }
        if (item.data.status === 'failed') break;
        if (!item.data.generationId) {
          // Do not accidentally open whichever other lesson was viewed last.
          navigate('/scan/result-video?generation=unavailable');
          break;
        }
        navigate(`${item.data.type === 'text' ? '/scan/result-text' : '/scan/result-video'}?generation=${encodeURIComponent(item.data.generationId)}`);
        break;
      case 'chat':
        navigate(`/ai-assistant?conversation=${encodeURIComponent(item.data.id)}`);
        break;
    }
  }

  function startEdit(item: UnifiedItem, e: MouseEvent) {
    e.stopPropagation();
    if (item.kind !== 'scan') return;
    setEditingId(item.data.id);
    setEditValue(item.data.title);
  }

  function commitEdit() {
    if (!editingId || !editValue.trim()) {
      setEditingId(null);
      return;
    }
    const trimmed = editValue.trim();
    renameScan(editingId, trimmed);
    renameStudySession(editingId, trimmed);
    setScanHistory(getScanHistory());
    setRefresh((r) => r + 1);
    setEditingId(null);
    setEditValue('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValue('');
  }

  function handleEditKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') cancelEdit();
  }

  return (
    <div className="study-history flex flex-col overflow-y-auto h-full" ref={scrollRef}>
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex-shrink-0 lg:px-8 lg:pt-6">
        <div className="flex items-center gap-3 mb-1">
          <Pressable
            onClick={() => navigate('/')}
            aria-label="Back to Home"
            className={`w-8 h-8 flex items-center justify-center rounded-lg ${isDark ? 'hover:bg-white/[0.08]' : 'hover:bg-neutral-100'} transition-colors lg:hidden`}
            scale={0.88}
          >
            <i className={`ri-arrow-left-line ${textMuted}`} />
          </Pressable>
          <div>
            <p className={`text-xs font-medium ${textSecondary}`}>Pick up where you left off</p>
            <h1 className={`text-xl font-bold leading-tight ${textPrimary}`}>Your learning history</h1>
          </div>
        </div>
      </div>

      <div className="px-5 lg:px-8 pb-5">
        <label htmlFor="history-search" className="sr-only">Search your study history</label>
        <div className={`flex items-center gap-3 px-4 rounded-2xl border ${cardBorder} ${cardBg}`}>
          <i className={`ri-search-line ${textMuted}`} aria-hidden="true" />
          <input id="history-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a question, topic, or lesson…" className={`w-full min-w-0 bg-transparent py-3.5 text-base ${textPrimary} outline-none`} />
        </div>
      </div>

      {/* Stats Bar — hidden until there is real activity */}
      {hasAnyStats && (
        <div className="px-5 pb-4 flex-shrink-0">
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar">
            {[
              { label: 'Watched', value: totalLessons, icon: 'ri-video-line' },
              { label: 'Quizzes', value: totalQuizzes, icon: 'ri-questionnaire-line' },
              { label: 'Topics', value: totalTopics, icon: 'ri-book-2-line' },
              { label: 'Scans', value: totalScans, icon: 'ri-scan-line' },
              { label: 'AI Chats', value: totalChats, icon: 'ri-chat-3-line' },
              { label: 'Saved', value: totalSaved, icon: 'ri-bookmark-line' },
            ].map((stat) => (
              <div
                key={stat.label}
                className={`flex-1 min-w-[76px] ${cardBg} rounded-xl py-3 px-2 border ${cardBorder} text-center`}
              >
                <p className={`text-lg font-bold ${textPrimary}`}>{stat.value}</p>
                <p className={`text-[10px] font-medium mt-0.5 ${textSecondary}`}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="px-5 pb-3 flex-shrink-0">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {[
            { id: 'all' as FilterType, label: 'All' },
          ].map((filter) => {
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                aria-pressed={isActive}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  isActive
                    ? isDark ? 'bg-[#F5F5F7] text-[#0F0F10]' : 'bg-neutral-900 text-white'
                    : isDark ? 'bg-white/[0.05] text-[#6E6E73] hover:bg-white/[0.08]' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                }`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content — Clean Text Rows */}
      <section className="px-5 pb-8">
        {hasContent ? (
          <div className="flex flex-col">
            {unifiedItems.map((item, index) => {
              const title = getItemTitle(item);
              const subject = getItemSubject(item);
              const date = formatTimeDisplay(getItemDate(item));
              const typeLabel = getItemUploadType(item);
              const status = getItemStatus(item);
              const progress = getItemProgress(item);
               const messageCount = getItemMessageCount(item);
              const icon = getItemIcon(item);
              const color = subjectColorMap[subject] || '#6E6E73';
              const isLast = index === unifiedItems.length - 1;
              const isEditing = editingId === item.data.id;

              return (
                <Pressable
                  as="div"
                  role="button"
                  tabIndex={isEditing ? -1 : 0}
                  aria-label={`Open ${title}`}
                  onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); handlePress(item); } }}
                  key={`${item.kind}-${item.data.id}-${refresh}`}
                  onClick={() => handlePress(item)}
                  className={`w-full text-left ${isLast ? '' : 'border-b'} ${rowBorder}`}
                  scale={0.98}
                >
                  <div className={`flex items-center gap-3.5 py-3.5 px-3 rounded-xl transition-colors ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-neutral-50'}`}>
                    {/* Icon */}
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${color}12` }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${icon} text-sm`} style={{ color }} />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            aria-label="New title for this scan"
                            onKeyDown={handleEditKeyDown}
                            onBlur={commitEdit}
                            autoFocus
                            className={`w-full text-[13px] font-semibold ${textPrimary} bg-transparent border-b ${isDark ? 'border-white/20' : 'border-neutral-300'} focus:outline-none`}
                          />
                        ) : (
                          <h4 className={`text-[13px] font-semibold ${textPrimary} truncate leading-snug`}>
                            {title}
                          </h4>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[11px] font-medium ${textMuted}`}>{subject}</span>
                        <span className={textFaint}>&middot;</span>
                        <span className={`text-[11px] ${textSecondary}`}>{typeLabel}</span>
                         {messageCount !== null && (
                           <>
                             <span className={textFaint}>&middot;</span>
                             <span className={`text-[11px] ${textSecondary}`}>{messageCount} {messageCount === 1 ? 'message' : 'messages'}</span>
                           </>
                         )}
                        <span className={textFaint}>&middot;</span>
                        <span className={`text-[11px] ${textSecondary}`}>{date}</span>
                      </div>

                      {/* Progress bar */}
                      {progress !== null && !isEditing && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className={`flex-1 h-[3px] rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} overflow-hidden max-w-[120px]`}>
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${progress}%`, backgroundColor: color }}
                            />
                          </div>
                          <span className={`text-[10px] font-medium ${textMuted}`}>{progress}%</span>
                        </div>
                      )}
                    </div>

                    {/* Right side: edit (scans only) + status + arrow */}
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className="flex items-center gap-1.5">
                        {!isEditing && item.kind === 'scan' && (
                          <button
                            type="button"
                            aria-label={`Rename ${title}`}
                            onClick={(e) => startEdit(item, e)}
                            className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/[0.06] text-[#6E6E73]' : 'hover:bg-neutral-100 text-neutral-400'}`}
                          >
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-pencil-line text-xs" />
                            </div>
                          </button>
                        )}
                        {isEditing && (
                          <button
                            type="button"
                            aria-label="Save new title"
                            onClick={(e) => { e.stopPropagation(); commitEdit(); }}
                            className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/[0.06] text-emerald-400' : 'hover:bg-neutral-100 text-emerald-600'}`}
                          >
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-check-line text-xs" />
                            </div>
                          </button>
                        )}
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${statusStyle(status, isDark)}`}>
                          {status}
                        </span>
                      </div>
                      {!isEditing && (
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`ri-arrow-right-s-line ${textFaint} text-sm`} />
                        </div>
                      )}
                    </div>
                  </div>
                </Pressable>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center text-center pt-12 pb-8">
            <div className={`w-16 h-16 rounded-2xl ${iconBg} flex items-center justify-center mb-5`}>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`ri-time-line ${textFaint} text-2xl`} />
              </div>
            </div>
            <h3 className={`text-base font-semibold ${textPrimary} mb-2`}>{query || activeFilter !== 'all' ? 'Nothing here just yet' : 'A fresh start for your studies'}</h3>
            <p className={`text-sm ${textMuted} max-w-[260px] leading-relaxed mb-6`}>
              {query ? 'Try another word or clear your search to see all your learning.' : activeFilter !== 'all' ? 'Your activity in this category will appear here after you study.' : 'Your questions, explanations, and lessons will be here when you need them again.'}
            </p>
            <Pressable
              onClick={() => { if (query || activeFilter !== 'all') { setQuery(''); setActiveFilter('all'); } else navigate('/'); }}
              className={`px-6 py-3 rounded-xl text-sm font-semibold ${isDark ? 'bg-white text-black' : 'bg-neutral-900 text-white'}`}
              scale={0.95}
            >
              {query || activeFilter !== 'all' ? 'Show all history' : 'Start learning'}
            </Pressable>
          </div>
        )}
      </section>
      {activeQuiz && location.pathname === '/history' && <QuizHistoryDetail quiz={activeQuiz} onClose={() => setActiveQuiz(null)} onPractice={() => { setActiveQuiz(null); navigate('/?tool=Quizify'); }} />}
    </div>
  );
}

function QuizHistoryDetail({ quiz, onClose, onPractice }: { quiz: HistoryQuiz; onClose: () => void; onPractice: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
    return () => dialogRef.current?.close();
  }, []);
  return <dialog ref={dialogRef} className="tool-start-dialog" aria-labelledby="quiz-history-title" onCancel={onClose} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className="tool-start-body"><div className="tool-start-top"><span className="tool-start-eyebrow">Completed quiz</span><button type="button" onClick={onClose} aria-label="Close quiz details"><i className="ri-close-line text-xl" /></button></div><h2 id="quiz-history-title">{quiz.title}</h2><p className="tool-start-description">{quiz.subject} · {new Date(quiz.completedAt).toLocaleDateString()}</p><p className="text-[42px] font-semibold tracking-tight mb-2">{quiz.score}<span className="text-[20px] text-neutral-500"> / {quiz.total}</span></p><p className="tool-start-description">Your recorded score. Keep practicing the ideas that need another look.</p><button type="button" className="tool-start-submit" onClick={onPractice}>Start another practice quiz</button></div></dialog>;
}